const atlas = new URL('../assets/game-orchard-rush-fruits-v1.png', import.meta.url).href;
/** Original raster fruit and bounded Canvas effects. Rendering consumes public
 * private-round geometry; no animation changes accepted hits, scores or time. */
export function createOrchardCanvas(canvas) {
  const g = canvas.getContext('2d');
  const image = new Image();
  image.src = atlas;
  let particles = [],
    awards = [],
    trail = [],
    clock = 0;
  function fruit(piece, alpha = 1, half = 0) {
    g.save();
    g.globalAlpha = alpha;
    g.translate(piece.x, piece.y);
    g.rotate(piece.rotation || 0);
    const r = piece.radius;
    if (half) {
      g.beginPath();
      g.rect(half < 0 ? -r * 1.35 : 0, -r * 1.35, r * 1.35, r * 2.7);
      g.clip();
    }
    if (piece.thorn) {
      g.fillStyle = '#191b3b';
      g.strokeStyle = '#ff5774';
      g.lineWidth = 3;
      g.beginPath();
      for (let i = 0; i < 20; i++) {
        const a = (i * Math.PI) / 10,
          d = i % 2 ? r * 0.64 : r;
        g.lineTo(Math.sin(a) * d, Math.cos(a) * d);
      }
      g.closePath();
      g.fill();
      g.stroke();
      g.fillStyle = '#fff';
      g.font = 'bold 21px system-ui';
      g.textAlign = 'center';
      g.fillText('×', 0, 7);
    } else if (image.complete && image.naturalWidth) {
      const w = image.naturalWidth / 3,
        h = image.naturalHeight / 2;
      g.drawImage(
        image,
        (piece.kind % 3) * w,
        Math.floor(piece.kind / 3) * h,
        w,
        h,
        -r * 1.35,
        -r * 1.35,
        r * 2.7,
        r * 2.7,
      );
    } else {
      const fill = g.createRadialGradient(-r * 0.3, -r * 0.35, 2, 0, 0, r);
      fill.addColorStop(0, '#fff');
      fill.addColorStop(0.23, piece.colour);
      fill.addColorStop(1, '#311336');
      g.fillStyle = fill;
      g.beginPath();
      g.arc(0, 0, r, 0, Math.PI * 2);
      g.fill();
    }
    g.restore();
  }
  function effects(events, reduced) {
    for (const event of events) {
      if (event.type === 'complete') continue;
      awards.push({
        x: event.x ?? 180,
        y: event.y ?? 240,
        born: clock,
        text:
          event.type === 'combo'
            ? `${event.count} FRUIT COMBO! +${event.points}`
            : event.type === 'thorn'
              ? 'WATCH THE THORNS'
              : `+${event.points}`,
        combo: event.type === 'combo',
        colour: event.type === 'thorn' ? '#ff7396' : '#fff8ab',
      });
      if (reduced || event.type !== 'slice') continue;
      for (const half of [-1, 1])
        particles.push({ ...event, half, born: clock, vx: half * 0.11, vy: -0.13, duration: 760 });
      for (let i = 0; i < 12; i++) {
        const angle = i * 2.39996 + event.id;
        particles.push({
          x: event.x,
          y: event.y,
          colour: event.colour,
          radius: 2 + (i % 4),
          vx: Math.cos(angle) * (0.09 + i * 0.006),
          vy: Math.sin(angle) * 0.18,
          born: clock,
          duration: 500 + i * 20,
        });
      }
    }
    particles = particles.slice(-180);
    awards = awards.slice(-14);
  }
  function swipe(point) {
    trail.push({ ...point, born: clock });
    trail = trail.slice(-24);
  }
  function draw(state, now, reduced = false) {
    clock = now;
    const ratio = Math.min(2, globalThis.devicePixelRatio || 1);
    const { width, height } = state.world;
    if (
      canvas.width !== Math.round(width * ratio) ||
      canvas.height !== Math.round(height * ratio)
    ) {
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
    }
    g.setTransform(ratio, 0, 0, ratio, 0, 0);
    // The rendered setting is a static CSS layer; moving pieces keep independent depth.
    g.clearRect(0, 0, width, height);
    for (const piece of state.fruits) fruit(piece);
    particles = particles.filter((p) => now - p.born < p.duration);
    if (!reduced)
      for (const p of particles) {
        const age = now - p.born,
          alpha = 1 - age / p.duration;
        const item = {
          ...p,
          x: p.x + p.vx * age,
          y: p.y + p.vy * age + 0.00045 * age * age,
          rotation: (p.rotation || 0) + (p.half || 1) * age * 0.003,
        };
        if (p.half) fruit(item, alpha, p.half);
        else {
          g.globalAlpha = alpha;
          g.fillStyle = p.colour;
          g.beginPath();
          g.arc(item.x, item.y, p.radius * alpha, 0, Math.PI * 2);
          g.fill();
        }
      }
    g.globalAlpha = 1;
    trail = trail.filter((p) => now - p.born < 140);
    if (trail.length > 1 && !reduced) {
      for (const [width, colour] of [
        [15, '#fdceff33'],
        [7, '#ffb4ed99'],
        [2, '#fff'],
      ]) {
        g.lineWidth = width;
        g.strokeStyle = colour;
        g.lineCap = 'round';
        g.lineJoin = 'round';
        g.beginPath();
        trail.forEach((p, i) => (i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y)));
        g.stroke();
      }
    }
    awards = awards.filter((a) => now - a.born < 1000);
    for (const award of awards) {
      const age = now - award.born;
      g.save();
      g.globalAlpha = Math.min(1, (1000 - age) / 230);
      g.textAlign = 'center';
      g.font = `900 ${award.combo ? 18 : 23}px system-ui`;
      const textWidth = g.measureText(award.text).width,
        x = Math.max(textWidth / 2 + 8, Math.min(width - 8 - textWidth / 2, award.x));
      const y = Math.max(35, award.y - (reduced ? 0 : age * 0.035));
      g.lineWidth = 4;
      g.strokeStyle = '#31102f';
      g.strokeText(award.text, x, y);
      g.fillStyle = award.colour;
      g.fillText(award.text, x, y);
      g.restore();
    }
  }
  return {
    draw,
    effects,
    swipe,
    clear() {
      particles = [];
      awards = [];
      trail = [];
    },
  };
}
